import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { rpc } from '@/lib/rpc';
import { supabase } from '@/lib/supabase';
import type {
  ActivityType,
  CallOutcome,
  Customer360,
  Lead,
  LeadSource,
  LeadStage,
} from '@/types/app';

export const leadKeys = {
  all: ['leads'] as const,
  list: (filters: LeadFilters, page: number) => ['leads', 'list', filters, page] as const,
  detail: (id: string) => ['leads', 'detail', id] as const,
};

// A single string literal so Supabase can infer the row type; '*' leaves every
// column `any`. See features/admin/useUsers.ts.
// prettier-ignore
const LEAD_COLUMNS = 'id, full_name, phone_primary, whatsapp_phone, email, client_type, temperature, budget_min, budget_max, preferred_areas, unit_types, bedrooms, payment_method, down_payment_capacity, purchase_horizon, source, captured_at, owner_id, team_id, stage, stage_entered_at, lost_reason, sla_due_at, first_contact_at, sla_status, next_action_type, next_action_at, next_action_note, last_activity_at, is_overdue, health, count_calls, count_whatsapp, count_notes, count_meetings, count_visits, count_offers, duplicate_hits, status, created_at, updated_at';

export interface LeadFilters {
  search?: string;
  stage?: LeadStage | '';
  source?: LeadSource | '';
  ownerId?: string;
  health?: string;
  slaStatus?: string;
}

export const PAGE_SIZE = 25;

export interface LeadPage {
  rows: Lead[];
  total: number;
}

/**
 * The lead list.
 *
 * Server-side filtering and pagination, not client-side: a rep on a phone should
 * download 25 rows, not the whole book. RLS already restricts which rows exist for
 * them, so there is no "only mine" filter — "mine" is simply what a sales user sees.
 */
export function useLeads(filters: LeadFilters, page: number) {
  return useQuery({
    queryKey: leadKeys.list(filters, page),
    // Keeps the previous page visible while the next loads, instead of flashing a
    // spinner on every keystroke of the search box.
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<LeadPage> => {
      let query = supabase
        .from('leads')
        .select(LEAD_COLUMNS, { count: 'exact' })
        .eq('status', 'active');

      if (filters.search?.trim()) {
        const term = `%${filters.search.trim()}%`;
        // Phone search works on whatever the user typed; they may paste a local
        // format while the column holds E.164, so match on the tail too.
        const digits = filters.search.replace(/[^0-9]/g, '');
        const phoneTerm = digits.length >= 4 ? `%${digits.slice(-9)}%` : term;
        query = query.or(
          `full_name.ilike.${term},email.ilike.${term},phone_primary.ilike.${phoneTerm}`,
        );
      }
      if (filters.stage) query = query.eq('stage', filters.stage);
      if (filters.source) query = query.eq('source', filters.source);
      if (filters.ownerId) query = query.eq('owner_id', filters.ownerId);
      if (filters.health) query = query.eq('health', filters.health);
      if (filters.slaStatus) query = query.eq('sla_status', filters.slaStatus);

      const from = page * PAGE_SIZE;
      const { data, error, count } = await query
        .order('captured_at', { ascending: false })
        .range(from, from + PAGE_SIZE - 1);

      if (error) throw error;
      return { rows: data ?? [], total: count ?? 0 };
    },
  });
}

/** Customer 360 (#9) — one call for lead, owner, team, timeline and counters. */
export function useCustomer360(leadId: string | undefined) {
  return useQuery({
    queryKey: leadId ? leadKeys.detail(leadId) : ['leads', 'detail', 'none'],
    enabled: Boolean(leadId),
    queryFn: () => rpc<Customer360>('customer_360', { p_lead_id: leadId!, p_activity_limit: 200 }),
  });
}

export interface CreateLeadInput {
  full_name: string;
  phone: string;
  source?: LeadSource;
  email?: string | null;
  whatsapp_phone?: string | null;
  client_type?: string | null;
  temperature?: string | null;
  budget_min?: number | null;
  budget_max?: number | null;
  preferred_areas?: string[];
  unit_types?: string[];
  bedrooms?: number | null;
  payment_method?: string | null;
  down_payment_capacity?: number | null;
  purchase_horizon?: string | null;
  owner_id?: string | null;
  team_id?: string | null;
}

/**
 * Creation goes through create_lead() — leads has no INSERT policy for any client
 * role, so this is the only route. A duplicate phone returns the EXISTING lead
 * rather than erroring, which the UI must handle as a normal outcome (#3).
 */
export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLeadInput) =>
      rpc<Lead>('create_lead', {
        p_full_name: input.full_name,
        p_phone: input.phone,
        p_source: input.source ?? 'other',
        p_email: input.email ?? null,
        p_whatsapp_phone: input.whatsapp_phone ?? null,
        p_client_type: input.client_type ?? null,
        p_temperature: input.temperature ?? null,
        p_budget_min: input.budget_min ?? null,
        p_budget_max: input.budget_max ?? null,
        p_preferred_areas: input.preferred_areas ?? [],
        p_unit_types: input.unit_types ?? [],
        p_bedrooms: input.bedrooms ?? null,
        p_payment_method: input.payment_method ?? null,
        p_down_payment_capacity: input.down_payment_capacity ?? null,
        p_purchase_horizon: input.purchase_horizon ?? null,
        p_owner_id: input.owner_id ?? null,
        p_team_id: input.team_id ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

/** Direct update of the columns a rep owns. The locked ones are revoked server-side. */
export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Lead> }) => {
      const { error } = await supabase.from('leads').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_d, { id }) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

export function useChangeStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      stage,
      lostReason,
      lostSubReason,
      notes,
    }: {
      id: string;
      stage: LeadStage;
      lostReason?: string;
      lostSubReason?: string;
      notes?: string;
    }) =>
      rpc<Lead>('change_lead_stage', {
        p_lead_id: id,
        p_stage: stage,
        p_lost_reason: lostReason ?? null,
        p_lost_sub_reason: lostSubReason ?? null,
        p_notes: notes ?? null,
      }),
    onSuccess: (_d, { id }) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

export function useTransferLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, newOwner, reason }: { id: string; newOwner: string; reason: string }) =>
      rpc<Lead>('transfer_lead_ownership', {
        p_lead_id: id,
        p_new_owner: newOwner,
        p_reason: reason,
      }),
    onSuccess: (_d, { id }) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

export interface LogActivityInput {
  leadId: string;
  type: ActivityType;
  body?: string | null;
  outcome?: CallOutcome | null;
  durationSeconds?: number | null;
}

/**
 * Manual activity logging. lead_activities is append-only and has no INSERT grant,
 * so this goes through log_lead_activity(), which also stamps the actor.
 */
export function useLogActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LogActivityInput) =>
      rpc<number>('log_lead_activity', {
        p_lead_id: input.leadId,
        p_type: input.type,
        p_body: input.body ?? null,
        p_outcome: input.outcome ?? null,
        p_duration_seconds: input.durationSeconds ?? null,
      }),
    onSuccess: (_d, { leadId }) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}
